import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Types
export interface UserContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'owner' | 'occupier' | 'agent';
}

export interface PropertyDetails {
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  propertyType: 'residential' | 'commercial' | 'industrial' | 'mixed';
  yearBuilt?: number;
  size?: number; // square feet/meters
  units?: number; // for multi-unit properties
}

export interface UserState {
  contact: UserContact | null;
  property: PropertyDetails | null;
  isContactComplete: boolean;
  isPropertyComplete: boolean;
  userToken: string | null;
  lastAssessmentDate: Date | null;
  assessmentHistory: string[]; // Array of assessment IDs
}

// Actions
type UserAction =
  | { type: 'SET_CONTACT'; payload: UserContact }
  | { type: 'SET_PROPERTY'; payload: PropertyDetails }
  | { type: 'SET_USER_TOKEN'; payload: string }
  | { type: 'ADD_ASSESSMENT'; payload: string }
  | { type: 'CLEAR_USER_DATA' };

// Initial state
const initialState: UserState = {
  contact: null,
  property: null,
  isContactComplete: false,
  isPropertyComplete: false,
  userToken: null,
  lastAssessmentDate: null,
  assessmentHistory: []
};

// Reducer
function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_CONTACT':
      const isContactComplete = !!(
        action.payload.firstName &&
        action.payload.lastName &&
        action.payload.email &&
        action.payload.phone &&
        action.payload.role
      );

      return {
        ...state,
        contact: action.payload,
        isContactComplete
      };

    case 'SET_PROPERTY':
      const isPropertyComplete = !!(
        action.payload.address.street &&
        action.payload.address.city &&
        action.payload.address.state &&
        action.payload.address.zipCode &&
        action.payload.propertyType
      );

      return {
        ...state,
        property: action.payload,
        isPropertyComplete
      };

    case 'SET_USER_TOKEN':
      return {
        ...state,
        userToken: action.payload
      };

    case 'ADD_ASSESSMENT':
      return {
        ...state,
        assessmentHistory: [...state.assessmentHistory, action.payload],
        lastAssessmentDate: new Date()
      };

    case 'CLEAR_USER_DATA':
      return initialState;

    default:
      return state;
  }
}

// Context
const UserContext = createContext<{
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
} | null>(null);

// Provider
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

// Hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};